import React, { useState } from 'react';
import { Play, RefreshCw, Check, ArrowRight, Info, Layers, GitFork, Ruler } from 'lucide-react';

// --- Constants & Graph Definition ---

const INITIAL_GRAPH_NODES = [
  { id: 0, label: 'A', x: 250, y: 50 },
  { id: 1, label: 'B', x: 150, y: 150 },
  { id: 2, label: 'C', x: 350, y: 150 },
  { id: 3, label: 'D', x: 50, y: 250 },
  { id: 4, label: 'E', x: 200, y: 250 },
  { id: 5, label: 'F', x: 300, y: 250 },
  { id: 6, label: 'G', x: 450, y: 250 },
];

const EDGES = [
  [0, 1], [0, 2], // A connects to B, C
  [1, 2],         // B connects to C
  [1, 3], [1, 4], // B connects to D, E
  [1, 5],         // B connects to F
  [2, 5], [2, 6], // C connects to F, G
  [4, 5]          // E connects to F
];

const ADJACENCY_LIST = {
  0: [1, 2],
  1: [0, 2, 3, 4, 5],
  2: [0, 1, 5, 6],
  3: [1],
  4: [1, 5],
  5: [1, 2, 4],
  6: [2]
};

// --- Components ---

const Node = ({ node, state, dist, isSelected, onClick, isNeighborTarget }) => {
  // Styles based on Layer-based BFS terminology
  let circleClass = "fill-white stroke-slate-400";
  let textClass = "fill-slate-700";

  // States: UNDISCOVERED, FINISHED, IN_CURRENT_LEVEL, CURRENT_PROCESSING
  switch (state) {
    case 'FINISHED':
      circleClass = "fill-emerald-100 stroke-emerald-500 stroke-2";
      textClass = "fill-emerald-800 font-bold";
      break;
    case 'CURRENT_PROCESSING': // The node 'v' in the loop
      circleClass = "fill-blue-500 stroke-blue-600";
      textClass = "fill-white font-bold";
      break;
    case 'IN_CURRENT_LEVEL': // In layers[i], waiting to be processed
      circleClass = "fill-amber-100 stroke-amber-500 stroke-2";
      textClass = "fill-amber-800 font-bold";
      break;
    case 'IN_NEXT_LEVEL': // Just added to layers[i+1]
      circleClass = "fill-purple-100 stroke-purple-500 stroke-2";
      textClass = "fill-purple-800 font-bold";
      break;
    case 'UNDISCOVERED':
    default:
      circleClass = "fill-slate-100 stroke-slate-300";
      textClass = "fill-slate-500";
      break;
  }

  if (isSelected) {
    circleClass = "fill-purple-200 stroke-purple-600 stroke-[3px]";
  }

  const pulseClass = isNeighborTarget ? "animate-pulse" : "";

  return (
    <g
      onClick={() => onClick(node.id)}
      className={`cursor-pointer transition-all duration-300 ${pulseClass}`}
    >
      <circle cx={node.x} cy={node.y} r={24} className={`${circleClass} transition-colors duration-300`} />
      <text x={node.x} y={node.y} dy=".35em" textAnchor="middle" className={`text-sm select-none pointer-events-none ${textClass}`}>
        {node.label}
      </text>
      {/* Display Distance Badge if discovered */}
      {dist !== Infinity && (
        <g transform={`translate(${node.x + 18}, ${node.y - 18})`}>
          <circle r="10" className="fill-slate-800 stroke-white stroke-2" />
          <text dy=".35em" textAnchor="middle" className="text-[10px] fill-white font-bold">{dist}</text>
        </g>
      )}
    </g>
  );
};

const Edge = ({ start, end, isActive, isTreeEdge }) => {
  let strokeClass = "stroke-slate-300";
  let strokeWidth = 2;

  if (isActive) {
    strokeClass = "stroke-blue-500";
    strokeWidth = 3;
  } else if (isTreeEdge) {
    strokeClass = "stroke-emerald-500"; // Tree edges are permanent green
    strokeWidth = 4;
  }

  return (
    <line
      x1={start.x} y1={start.y} x2={end.x} y2={end.y}
      strokeWidth={strokeWidth}
      className={`transition-colors duration-500 ${strokeClass} ${isTreeEdge ? 'opacity-100' : 'opacity-60'}`}
    />
  );
};

// --- Main Application ---

export default function BFSLearningTool() {
  // --- State for Layer-Based BFS ---
  // layers: dictionary where key is layer index, value is array of nodes
  const [layers, setLayers] = useState({ 0: [] });
  const [currentLayerIndex, setCurrentLayerIndex] = useState(0);

  // dist: dictionary mapping node ID to distance
  const [dist, setDist] = useState({});

  // tree: dictionary mapping node ID to parent node ID
  const [tree, setTree] = useState({});

  // Control flow state
  const [finishedNodes, setFinishedNodes] = useState(new Set());
  const [processedInCurrentLayer, setProcessedInCurrentLayer] = useState(new Set()); // Nodes in layers[i] we have already done the 'for' loop for
  const [currentNode, setCurrentNode] = useState(null); // The 'v' in 'for v in layers[i]'

  const [step, setStep] = useState('START');
  // Steps: START, LAYER_START, PICK_NODE, IDENTIFY_NEIGHBORS, FINISH_NODE, LAYER_COMPLETE, FINISHED_ALL

  const [selectedNeighbors, setSelectedNeighbors] = useState([]);
  const [message, setMessage] = useState("Welcome! Click 'Start BFS' to initialize Layer 0.");
  const [error, setError] = useState("");

  // --- Reset ---
  const resetBFS = () => {
    setLayers({ 0: [] });
    setCurrentLayerIndex(0);
    setDist({});
    setTree({});
    setFinishedNodes(new Set());
    setProcessedInCurrentLayer(new Set());
    setCurrentNode(null);
    setStep('START');
    setSelectedNeighbors([]);
    setMessage("Welcome! Click 'Start BFS' to initialize Layer 0.");
    setError("");
  };

  // --- Actions ---

  const startBFS = () => {
    // Algorithm: layers <- {}, dist <- {}, tree <- {}
    // layers[0] = [s], dist[s] = 0
    const startNode = 0;

    // Initialize standard state for all nodes first (for visualization consistency)
    const initialDist = {};
    INITIAL_GRAPH_NODES.forEach(n => initialDist[n.id] = Infinity);
    initialDist[startNode] = 0;

    setLayers({ 0: [startNode] });
    setDist(initialDist);
    setTree({});
    setCurrentLayerIndex(0);

    setStep('LAYER_START');
    setMessage(`Initialized! Layer 0 contains the source node (${INITIAL_GRAPH_NODES[startNode].label}). Distance is 0.`);
  };

  const startNextLayer = () => {
    const currentLayerNodes = layers[currentLayerIndex] || [];

    if (currentLayerNodes.length === 0) {
      setStep('FINISHED_ALL');
      setMessage("Current layer is empty. BFS Complete!");
      return;
    }

    // Prepare for loop: for v in layers[i]
    setStep('PICK_NODE');
    // Initialize next layer empty array if not exists
    setLayers(prev => ({...prev, [currentLayerIndex + 1]: []}));
    setMessage(`Starting Layer ${currentLayerIndex}. Processing nodes: [${currentLayerNodes.map(id => INITIAL_GRAPH_NODES[id].label).join(', ')}]`);
  };

  const pickNextNode = () => {
    const currentLayerNodes = layers[currentLayerIndex];
    // Find first node in this layer that hasn't been processed
    const nextNode = currentLayerNodes.find(n => !processedInCurrentLayer.has(n));

    if (nextNode === undefined) {
      // Loop for this layer is done
      setStep('LAYER_COMPLETE');
      setMessage(`Layer ${currentLayerIndex} processing complete. Moving to Layer ${currentLayerIndex + 1}.`);
    } else {
      setCurrentNode(nextNode);
      setStep('IDENTIFY_NEIGHBORS');
      setSelectedNeighbors([]);
      setMessage(`Processing node ${INITIAL_GRAPH_NODES[nextNode].label}. Select its Undiscovered neighbors to add to Layer ${currentLayerIndex + 1}.`);
    }
  };

  const handleNodeClick = (id) => {
    if (step !== 'IDENTIFY_NEIGHBORS') return;
    if (id === currentNode) return;

    // Toggle selection
    if (selectedNeighbors.includes(id)) {
      setSelectedNeighbors(prev => prev.filter(n => n !== id));
      setError("");
    } else {
      // Logic: neighbor must be connected AND dist[u] == infinity
      const neighbors = ADJACENCY_LIST[currentNode];
      if (!neighbors.includes(id)) {
        setError(`Node ${INITIAL_GRAPH_NODES[id].label} is not connected to ${INITIAL_GRAPH_NODES[currentNode].label}.`);
        return;
      }

      if (dist[id] !== Infinity) {
        setError(`Node ${INITIAL_GRAPH_NODES[id].label} is already discovered (Dist: ${dist[id]}). BFS tree only adds undiscovered nodes.`);
        return;
      }

      setSelectedNeighbors(prev => [...prev, id]);
      setError("");
    }
  };

  const confirmNeighbors = () => {
    // Validate
    const allNeighbors = ADJACENCY_LIST[currentNode];
    const trueUndiscovered = allNeighbors.filter(n => dist[n] === Infinity);

    const missing = trueUndiscovered.filter(n => !selectedNeighbors.includes(n));
    if (missing.length > 0) {
      setError(`You missed Undiscovered neighbors: ${missing.map(id => INITIAL_GRAPH_NODES[id].label).join(', ')}`);
      return;
    }

    // Apply updates
    // 1. Add to layers[i+1]
    // 2. dist[u] = i + 1
    // 3. tree[u] = v
    const nextLayerIdx = currentLayerIndex + 1;

    setLayers(prev => ({
      ...prev,
      [nextLayerIdx]: [...(prev[nextLayerIdx] || []), ...selectedNeighbors]
    }));

    setDist(prev => {
      const nextDist = { ...prev };
      selectedNeighbors.forEach(n => nextDist[n] = nextLayerIdx);
      return nextDist;
    });

    setTree(prev => {
      const nextTree = { ...prev };
      selectedNeighbors.forEach(n => nextTree[n] = currentNode);
      return nextTree;
    });

    // Mark current node processed
    setProcessedInCurrentLayer(prev => new Set([...prev, currentNode]));
    setFinishedNodes(prev => new Set([...prev, currentNode]));

    setStep('PICK_NODE'); // Go back to pick next node in layer
    setMessage(`Added ${selectedNeighbors.length} node(s) to Layer ${nextLayerIdx}. Node ${INITIAL_GRAPH_NODES[currentNode].label} Finished.`);
    setCurrentNode(null);
  };

  const finishLayer = () => {
    setCurrentLayerIndex(prev => prev + 1);
    setProcessedInCurrentLayer(new Set()); // Reset for new layer
    setStep('LAYER_START');
  };

  // --- Rendering Helpers ---

  const getNodeState = (id) => {
    if (id === currentNode) return 'CURRENT_PROCESSING';

    // Is it in the next layer (just added)?
    if (layers[currentLayerIndex + 1]?.includes(id)) return 'IN_NEXT_LEVEL';

    // Is it in the current layer waiting to be processed?
    if (layers[currentLayerIndex]?.includes(id) && !processedInCurrentLayer.has(id)) return 'IN_CURRENT_LEVEL';

    // Is it completely finished?
    if (finishedNodes.has(id)) return 'FINISHED';

    return 'UNDISCOVERED';
  };

  // --- Render ---

  return (
    <div className="min-h-screen bg-slate-50 p-4 font-sans text-slate-800">
      <div className="max-w-6xl mx-auto">

        <header className="mb-6 border-b border-slate-200 pb-4">
          <h1 className="text-2xl font-bold text-slate-900">BFS: Layer-by-Layer Visualization</h1>
          <p className="text-slate-600 text-sm mt-1">
            Build the BFS Tree by exploring layers. <code className="bg-slate-100 px-1 rounded">layers[0]</code> is the source.
            For each node in <code className="bg-slate-100 px-1 rounded">layers[i]</code>, we find undiscovered neighbors to build <code className="bg-slate-100 px-1 rounded">layers[i+1]</code>.
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

          {/* LEFT: GRAPH (8 cols) */}
          <div className="lg:col-span-8 flex flex-col gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 relative min-h-[400px]">

              {/* Legend */}
              <div className="flex flex-wrap gap-4 mb-4 text-xs font-medium text-slate-500 justify-center border-b border-slate-100 pb-2">
                <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-slate-100 border border-slate-300"></div> Undiscovered</div>
                <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-amber-100 border border-amber-500"></div> In Current Level</div>
                <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-blue-500 border border-blue-600"></div> Current Node</div>
                <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-purple-100 border border-purple-500"></div> In Next Level</div>
                <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-emerald-100 border border-emerald-500"></div> Finished</div>
              </div>

              <svg width="100%" height="350" viewBox="0 0 500 300" className="select-none mx-auto">
                {/* Edges */}
                {EDGES.map(([u, v], i) => {
                  const nodeU = INITIAL_GRAPH_NODES[u];
                  const nodeV = INITIAL_GRAPH_NODES[v];
                  // Tree edge if parent[v] == u or parent[u] == v
                  const isTree = (tree[v] === u) || (tree[u] === v);
                  const isActive = (u === currentNode && selectedNeighbors.includes(v)) || (v === currentNode && selectedNeighbors.includes(u));

                  return <Edge key={i} start={nodeU} end={nodeV} isActive={isActive} isTreeEdge={isTree} />;
                })}
                {/* Nodes */}
                {INITIAL_GRAPH_NODES.map(node => (
                  <Node
                    key={node.id}
                    node={node}
                    dist={dist[node.id]}
                    state={getNodeState(node.id)}
                    isSelected={selectedNeighbors.includes(node.id)}
                    onClick={handleNodeClick}
                    isNeighborTarget={
                      step === 'IDENTIFY_NEIGHBORS' &&
                      currentNode !== null &&
                      ADJACENCY_LIST[currentNode].includes(node.id) &&
                      dist[node.id] === Infinity
                    }
                  />
                ))}
              </svg>
            </div>

            {/* MESSAGE / CONTROLS */}
            <div className="bg-white p-4 rounded-xl shadow-lg border-l-4 border-blue-500 flex flex-col gap-3">
               <div className="text-lg font-medium text-slate-800">{message}</div>
               {error && <div className="text-red-600 text-sm bg-red-50 p-2 rounded flex items-center gap-2"><Info size={14}/>{error}</div>}

               <div className="flex gap-2 mt-2">
                 {step === 'START' && (
                   <button onClick={startBFS} className="bg-blue-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-blue-700 flex items-center gap-2"><Play size={16}/> Start Algorithm</button>
                 )}
                 {step === 'LAYER_START' && (
                   <button onClick={startNextLayer} className="bg-blue-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-blue-700 flex items-center gap-2"><ArrowRight size={16}/> Process Layer {currentLayerIndex}</button>
                 )}
                 {step === 'PICK_NODE' && (
                   <button onClick={pickNextNode} className="bg-blue-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-blue-700 flex items-center gap-2">Pick Next Node</button>
                 )}
                 {step === 'IDENTIFY_NEIGHBORS' && (
                   <button onClick={confirmNeighbors} className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-indigo-700 flex items-center gap-2"><Check size={16}/> Confirm Neighbors</button>
                 )}
                 {step === 'LAYER_COMPLETE' && (
                   <button onClick={finishLayer} className="bg-emerald-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-emerald-700 flex items-center gap-2"><Layers size={16}/> Move to Layer {currentLayerIndex + 1}</button>
                 )}
                 {step === 'FINISHED_ALL' && (
                    <button onClick={resetBFS} className="bg-slate-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-slate-700 flex items-center gap-2"><RefreshCw size={16}/> Restart</button>
                 )}
               </div>
            </div>
          </div>

          {/* RIGHT: DATA STRUCTURES (4 cols) */}
          <div className="lg:col-span-4 flex flex-col gap-4">

            {/* LAYERS PANEL */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex-1">
              <h3 className="font-bold text-slate-400 uppercase tracking-wider text-sm flex items-center gap-2 mb-3">
                <Layers size={16} /> Layers Dictionary
              </h3>
              <div className="space-y-2 font-mono text-sm max-h-[150px] overflow-y-auto">
                {Object.keys(layers).map(layerIdx => (
                  <div key={layerIdx} className={`p-2 rounded border ${parseInt(layerIdx) === currentLayerIndex ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-100'}`}>
                    <span className="font-bold text-slate-500 mr-2">Layer {layerIdx}:</span>
                    <span>
                      [{layers[layerIdx].map(id => INITIAL_GRAPH_NODES[id].label).join(', ')}]
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* DIST PANEL */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex-1">
               <h3 className="font-bold text-slate-400 uppercase tracking-wider text-sm flex items-center gap-2 mb-3">
                <Ruler size={16} /> Dist Dictionary
              </h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {INITIAL_GRAPH_NODES.map(node => (
                  <div key={node.id} className="flex justify-between p-2 bg-slate-50 rounded border border-slate-100">
                    <span className="font-bold">{node.label}</span>
                    <span className={dist[node.id] === Infinity ? 'text-slate-400' : 'text-blue-600 font-bold'}>
                      {dist[node.id] === Infinity ? '∞' : dist[node.id]}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* TREE PANEL */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex-1">
               <h3 className="font-bold text-slate-400 uppercase tracking-wider text-sm flex items-center gap-2 mb-3">
                <GitFork size={16} /> Tree Dictionary
              </h3>
              <div className="text-sm bg-slate-50 p-3 rounded border border-slate-100 min-h-[60px]">
                {Object.keys(tree).length === 0 ? <span className="text-slate-400 italic">Empty</span> : (
                  <div className="grid grid-cols-2 gap-x-4">
                    {Object.entries(tree).map(([childId, parentId]) => (
                      <div key={childId} className="flex items-center gap-2">
                        <span className="font-bold">{INITIAL_GRAPH_NODES[childId].label}</span>
                        <ArrowRight size={12} className="text-slate-400"/>
                        <span className="text-slate-600">{INITIAL_GRAPH_NODES[parentId].label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

          </div>

        </div>
      </div>
    </div>
  );
}